import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import router from "@/router";
// import baseUrl from "@/config/appConfig";
import { ElMessage } from "element-plus";
import { generateUniqueId } from "./common";

/**
 * 因为这个类型跟axios.d.ts中的ResponseDataType冲突，所以废弃
 * 直接使用axios.d.ts中的ResponseDataType
 */
export interface ResponseType<T = any> {
  code: number;
  msg: string;
  data: T;
}

let messageInstance: any = null;
const resetMessage = (options: any) => {
  //关闭上一个弹窗，继续下一个弹窗
  if (messageInstance) {
    messageInstance.close();
  }
  messageInstance = ElMessage(options);
};

// 请求拦截器: 处理全局配置
axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token: string = localStorage.getItem("token_value") || "";
    const requestId = generateUniqueId();
    const timestamp = Date.now().toString();
    if (config.headers) {
      config.headers.authentication = token;
      config.headers["requestId"] = requestId;
      config.headers["timestamp"] = timestamp;
    } else {
      config.headers = {
        authentication: token,
        requestId,
        timestamp,
      };
    }
    return config;
  },
  (err) => Promise.reject(err)
);

// 响应拦截器: 处理HTTP错误
axios.interceptors.response.use(
  (response: AxiosResponse) => {
    /**
     * closeDefaultMessage: 是否关闭默认提示，默认false 开启提示
     */
    if (
      !response.config.headers?.closeDefaultMessage &&
      response.data.code !== 0
    ) {
      ElMessage.error(response.data.msg || "请求错误");
    }
    return response.data;
  },
  (err: AxiosError) => {
    const { response } = err;
    // 统一处理HTTP错误
    if (response) {
      const { status } = response;
      switch (status) {
        case 404:
          resetMessage({
            message: "请求发生404错误",
            type: "error",
          });
          break;
        case 500:
          resetMessage({
            message: "请求发生500错误",
            type: "error",
          });
          break;
        case 504:
          resetMessage({
            message: "请求超时",
            type: "error",
          });
          break;
        // case 400: // 用户没有csrf-token
        case 401: // 用户没有登录态
          handleUnauthorized();
          break;
        default:
          resetMessage({
            message: "网络异常",
            type: "error",
          });
          break;
      }
    } else {
      resetMessage({
        message: "网络异常",
        type: "error",
      });
    }
    return err;
  }
);

/**
 * 处理未授权（401）逻辑
 */
function handleUnauthorized() {
  const { pathname, search } = window.location;

  sessionStorage.setItem("redirectUrl", pathname + search); // 记录当前路由URL
  resetMessage({
    message: "请重新登录",
    type: "error",
  });
  setTimeout(() => {
    router.replace(`/login`);
  }, 2000);
}
/** 响应数据类型守卫 */
function isResponseData<T>(responseData: ResponseDataType<T>) {
  return responseData && typeof responseData.code === "number";
}
/**
 * 处理业务响应状态码
 * @param res 响应数据
 * @param config 请求配置
 */
function handleBizResponse<T>(
  responseData: ResponseDataType<T>,
  config?: RequestConfig<T>
): T;
function handleBizResponse<T>(
  responseData: ResponseDataType<T>,
  config: RequestConfig<T> & { skipBizCheck: true }
): any;
function handleBizResponse<T>(
  responseData: ResponseDataType<T>,
  config?: RequestConfig<T>
): T | ResponseDataType<T> {
  // 跳过业务状态码检查（如导出文件等特殊场景）
  if (config?.skipBizCheck) return responseData as any;

  const { code, data, msg } = responseData;

  // 校验响应数据结构
  if (!isResponseData(responseData)) {
    throw new Error("无效响应结构");
  }

  // 处理业务错误（code !== 0）
  if (code !== 0) {
    // 如果配置了空数据处理函数，则调用空数据处理函数
    if (config?.emptyDataHandler) {
      return config.emptyDataHandler();
    }
    throw new Error(msg);
  }

  return data;
}

// 请求配置类型
export interface RequestConfig<T = any> extends AxiosRequestConfig {
  /** 是否关闭默认提示，默认false 开启提示， */
  closeDefaultMessage?: boolean;
  /** 是否跳过业务状态码校验，默认false, 后续备用在文件导出中可能用到 */
  skipBizCheck?: boolean;
  /** 自定义空数据返回 */
  emptyDataHandler?: () => T;
}

/**
 * GET 请求方法
 * @param url 请求地址
 * @param params 请求参数
 * @param config 请求配置
 * @returns Promise<T> 直接返回 data 数据
 */
export async function GET<T = any>(
  url: string,
  params?: any,
  config?: RequestConfig<T>
): Promise<T> {
  try {
    const res = await axios.get<T>(url, {
      params,
      ...config,
    });
    return handleBizResponse(res, config);
  } catch (error) {
    return Promise.reject(error);
  }
}

/**
 * POST 请求方法
 * @param url 请求地址
 * @param params 请求数据
 * @param config 请求配置
 * @returns Promise<T> 直接返回 data 数据
 */
export async function POST<T = any, D = any>(
  url: string,
  params?: D,
  config?: RequestConfig<T>
): Promise<T> {
  try {
    const res = await axios.post<T>(url, params, config);

    return handleBizResponse(res, config);
  } catch (error) {
    return Promise.reject(error);
  }
}

/**
 * POST方式下载文件
 * @param url 请求地址
 * @param data 请求数据
 * @param config 请求配置
 * @returns Promise<Blob> 返回文件的Blob数据
 */
export async function POST_DOWNLOAD<T = any>(
  url: string,
  data?: T,
  config?: RequestConfig
): Promise<Blob> {
  try {
    const response = (await axios.post<Blob>(url, data, {
      ...config,
      responseType: "blob",
    })) as unknown as Blob;
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
}

export default axios;
